import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus,
  Trash2,
  ExternalLink,
  FileText,
  FileWarning,
  Loader2,
  RotateCcw,
  CheckCircle2,
  XCircle,
  Search,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import adminApi, { STATIC_ORIGIN } from "./adminApi";

// function attachmentUrl(filename) {
//   if (!filename) return null;
//   return `${STATIC_ORIGIN}/images/menudocuments/${filename}`;
// }

function attachmentUrl(filename) {
  if (!filename) return null;

  // Supports both old full path and new filename-only value
  const fileName = filename.split(/[\\/]/).pop();

  return `${STATIC_ORIGIN}/images/menudocuments/${fileName}`;
}

function groupByParent(items) {
  const groups = [];
  let current = null;

  for (const item of items) {
    if (!current || current.parent_pageid !== item.parent_pageid) {
      current = {
        parent_pageid: item.parent_pageid,
        parent_name: item.parent_name,
        rows: [],
      };

      groups.push(current);
    }

    current.rows.push(item);
  }

  return groups;
}

export default function ManageSubMenu() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [filterParents, setFilterParents] = useState([]);

  const [parentQ, setParentQ] = useState("");
  const [statusQ, setStatusQ] = useState("");

  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });

    setTimeout(() => {
      setToast(null);
    }, 2600);
  };

  // ---------------------------------------------------------
  // Fetch filter parents
  // ---------------------------------------------------------

  const fetchFilterParents = async () => {
    try {
      const res = await adminApi.get("/admin/sub-menu");

      const unique = [];
      const seen = new Set();

      (res.data.items || []).forEach((i) => {
        if (!seen.has(i.parent_pageid)) {
          seen.add(i.parent_pageid);

          unique.push({
            parent_pageid: i.parent_pageid,
            parent_name: i.parent_name,
          });
        }
      });

      unique.sort((a, b) =>
        a.parent_name.localeCompare(b.parent_name)
      );

      setFilterParents(unique);
    } catch (err) {
      console.error("Could not load filter parent list:", err);
    }
  };

  // ---------------------------------------------------------
  // Fetch items
  // ---------------------------------------------------------

  const fetchItems = async (overrides = {}) => {
    setLoading(true);
    setError("");

    const params = {
      parent: overrides.parentQ ?? parentQ,
      status: overrides.statusQ ?? statusQ,
    };

    try {
      const res = await adminApi.get("/admin/sub-menu", {
        params,
      });

      setItems(res.data.items || []);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Could not load sub menu contents."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterParents();
    fetchItems();
  }, []);

  // ---------------------------------------------------------
  // Search
  // ---------------------------------------------------------

  const handleSearch = (e) => {
    e.preventDefault();

    fetchItems();
  };

  const handleReset = () => {
    setParentQ("");
    setStatusQ("");

    fetchItems({
      parentQ: "",
      statusQ: "",
    });
  };

  // ---------------------------------------------------------
  // Selection
  // ---------------------------------------------------------

  const toggleSelectAll = () => {
    setSelected(
      selected.length === items.length
        ? []
        : items.map((i) => i.page_id)
    );
  };

  const toggleSelectOne = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  // ---------------------------------------------------------
  // Status
  // ---------------------------------------------------------

  const handleToggleStatus = async (item, e) => {
    e.stopPropagation();

    const turningOn = item.page_status !== 1;

    const ok = window.confirm(
      `Are you sure you want to change the status to ${
        turningOn ? "Active" : "Inactive"
      }?`
    );

    if (!ok) return;

    try {
      const res = await adminApi.patch(
        `/admin/sub-menu/${item.page_id}/status`
      );

      setItems((prev) =>
        prev.map((i) =>
          i.page_id === item.page_id
            ? {
                ...i,
                page_status: res.data.page_status,
              }
            : i
        )
      );
    } catch (err) {
      showToast(
        "error",
        err.response?.data?.message ||
          "Could not update status."
      );
    }
  };

  // ---------------------------------------------------------
  // Bulk delete
  // ---------------------------------------------------------

  const handleBulkDelete = async () => {
    if (selected.length === 0) return;

    const ok = window.confirm(
      `Are you sure you want to delete ${selected.length} selected record(s)? This cannot be undone.`
    );

    if (!ok) return;

    setBulkDeleting(true);

    try {
      await adminApi.post(
        "/admin/sub-menu/bulk-delete",
        {
          ids: selected,
        }
      );

      showToast(
        "success",
        `${selected.length} record(s) deleted.`
      );

      setSelected([]);

      fetchItems();
      fetchFilterParents();
    } catch (err) {
      showToast(
        "error",
        err.response?.data?.message ||
          "Could not delete selected records."
      );
    } finally {
      setBulkDeleting(false);
    }
  };

  const allSelected =
    items.length > 0 &&
    selected.length === items.length;

  const groups = groupByParent(items);

  return (
    <div className="h-full flex flex-col">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="shrink-0 mb-2">

        <div className="flex items-center justify-between gap-3 mb-2">

          <div>
            <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
              Manage Sub Menu Contents
            </h3>
          </div>

          {/* FILTER */}

          <form
            onSubmit={handleSearch}
            className="border-slate-200 rounded-lg flex flex-wrap items-center gap-3"
          >

            <div className="flex items-center gap-1.5">

              <label className="text-xs font-medium text-slate-500 whitespace-nowrap">
                Menu Name:
              </label>

              <select
                value={parentQ}
                onChange={(e) =>
                  setParentQ(e.target.value)
                }
                className="min-w-[170px] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                style={{ background: "#fff" }}
              >
                <option value="">-- All --</option>

                {filterParents.map((p) => (
                  <option
                    key={p.parent_pageid}
                    value={p.parent_pageid}
                  >
                    {p.parent_name}
                  </option>
                ))}
              </select>

            </div>

            <div className="flex items-center gap-1.5">

              <label className="text-xs font-medium text-slate-500 whitespace-nowrap">
                Status:
              </label>

              <select
                value={statusQ}
                onChange={(e) =>
                  setStatusQ(e.target.value)
                }
                className="min-w-[130px] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                style={{ background: "#fff" }}
              >
                <option value="">-- All --</option>
                <option value="1">Active</option>
                <option value="2">Inactive</option>
              </select>

            </div>

            <div className="flex items-center gap-1.5">

              <button
                type="submit"
                className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
              >
                <Search size={13} />
                Search
              </button>

              <button
                type="button"
                onClick={handleReset}
                title="Reset filters"
                className="flex items-center gap-1.5 text-slate-400 hover:text-slate-700 text-[13px] px-1.5 py-1.5 rounded-lg transition"
                style={{ background: "#fff" }}
              >
                <RotateCcw size={13} />
              </button>

            </div>

          </form>

          {/* ACTIONS */}

          <div className="flex items-center gap-2">

            <AnimatePresence>
              {selected.length > 0 && (
                <motion.button
                  initial={{
                    opacity: 0,
                    x: 8,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  exit={{
                    opacity: 0,
                    x: 8,
                  }}
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="flex items-center gap-1.5 bg-red-50 text-red-600 hover:bg-red-100 text-[13px] font-medium px-3 py-1.5 rounded-lg transition disabled:opacity-60"
                >
                  {bulkDeleting ? (
                    <Loader2
                      size={15}
                      className="animate-spin"
                    />
                  ) : (
                    <Trash2 size={15} />
                  )}

                  Delete Selected ({selected.length})
                </motion.button>
              )}
            </AnimatePresence>

            {/* ADD NEW */}

            <button
              onClick={() =>
                navigate("/admin/contents/sub-menu/add")
              }
              className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
            >
              <Plus size={15} />
              Add New
            </button>

          </div>

        </div>
      </div>

      {/* =====================================================
          TABLE
      ===================================================== */}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">

        {loading ? (

          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2
              size={18}
              className="animate-spin"
            />
            Loading...
          </div>

        ) : error ? (

          <div className="py-16 text-center text-sm text-red-500">
            {error}
          </div>

        ) : items.length === 0 ? (

          <div className="py-16 text-center">
            <FileText
              size={28}
              className="mx-auto text-slate-300 mb-2"
            />

            <p className="text-sm text-slate-400">
              No records yet — click "Add New" to create one.
            </p>
          </div>

        ) : (

          <div className="flex-1 min-h-0 overflow-auto">

            <table className="w-full text-sm border-collapse">

              <thead className="sticky top-0 z-10 shadow-md">

                <tr className="text-left text-base">

                  <th
                    className="th-colorful w-8 border-b-2 border-r normal-case"
                    style={{
                      padding: "5px 10px",
                      lineHeight: "1px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                    />
                  </th>

                  <th
                    className="th-colorful font-bold uppercase tracking-wide border-b-2 border-r"
                    style={{
                      padding: "5px 10px",
                      lineHeight: "1px",
                    }}
                  >
                    Sub Menu Name
                  </th>

                  <th
                    className="th-colorful font-bold uppercase tracking-wide border-b-2 border-r"
                    style={{
                      padding: "5px 10px",
                      lineHeight: "1px",
                      textAlign: "center",
                    }}
                  >
                    Attachment
                  </th>

                  <th
                    className="th-colorful font-bold uppercase tracking-wide border-b-2 border-r"
                    style={{
                      padding: "5px 10px",
                    }}
                  >
                    Sequence
                  </th>

                  <th
                    className="th-colorful font-bold uppercase tracking-wide border-b-2 border-r"
                    style={{
                      padding: "5px 10px",
                    }}
                  >
                    Is Content?
                  </th>

                  <th
                    className="th-colorful font-bold uppercase tracking-wide text-center border-b-2"
                    style={{
                      padding: "5px 10px",
                    }}
                  >
                    Status
                  </th>

                </tr>

              </thead>

              <tbody>

                {groups.map((group) => (

                  <React.Fragment
                    key={group.parent_pageid}
                  >

                    {/* PARENT */}

                    <tr className="bg-slate-100/70">

                      <td
                        colSpan={6}
                        className="text-xs font-semibold text-[#0B2545] border-b border-slate-200"
                        style={{
                          padding: "3px 10px",
                        }}
                      >
                        {group.parent_name}
                      </td>

                    </tr>

                    {/* CHILDREN */}

                    <AnimatePresence initial={false}>

                      {group.rows.map((it, i) => (

                        <motion.tr
                          key={it.page_id}
                          layout
                          initial={{
                            opacity: 0,
                          }}
                          animate={{
                            opacity: 1,
                          }}
                          exit={{
                            opacity: 0,
                          }}
                          transition={{
                            duration: 0.2,
                            delay: i * 0.02,
                          }}

                          /* ROW CLICK = EDIT PAGE */

                          // onClick={() =>
                          //   navigate(
                          //     `/admin/contents/sub-menu/edit/${it.page_id}`
                          //   )
                          // }

                          onClick={() =>
                            navigate("/admin/contents/sub-menu/edit", {
                              state: {
                                page_id: it.page_id
                              }
                            })
                          }

                          className={`cursor-pointer hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200 ${
                            selected.includes(
                              it.page_id
                            )
                              ? "bg-[#0EA5A4]/15"
                              : ""
                          }`}
                        >

                          <td
                            className="border-r border-slate-200"
                            onClick={(e) =>
                              e.stopPropagation()
                            }
                            style={{
                              padding: "3px 10px",
                            }}
                          >

                            <input
                              type="checkbox"
                              checked={selected.includes(
                                it.page_id
                              )}
                              onChange={() =>
                                toggleSelectOne(
                                  it.page_id
                                )
                              }
                              className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                            />

                          </td>

                          <td
                            className="text-slate-800 font-medium border-r border-slate-200"
                            style={{
                              padding: "3px 10px",
                            }}
                          >
                            {it.page_name}
                          </td>

                          <td
                            className="border-r border-slate-200"
                            style={{
                              padding: "3px 10px",
                              textAlign: "center",
                            }}
                          >

                            {it.attachment ? (

                              <a
                                href={attachmentUrl(
                                  it.attachment
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) =>
                                  e.stopPropagation()
                                }
                                className="inline-flex items-center justify-center text-[#0EA5A4] hover:underline"
                              >

                                <div
                                  style={{
                                    border:
                                      "0.5px solid #999",
                                    padding: "7px",
                                    marginTop: "3px",
                                    marginBottom: "3px",
                                    borderRadius: "7px",
                                  }}
                                >
                                  <i
                                    style={{
                                      color: "green",
                                    }}
                                    className="fa fa-eye"
                                    aria-hidden="true"
                                  ></i>
                                </div>

                              </a>

                            ) : (

                              <span className="inline-flex items-center gap-1.5 text-slate-300 text-xs">
                                <FileWarning
                                  size={14}
                                />
                                None
                              </span>

                            )}

                          </td>

                          <td
                            className="text-slate-500 border-r border-slate-200"
                            style={{
                              padding: "3px 10px",
                            }}
                          >
                            {it.seq_id}
                          </td>

                          <td
                            className="text-slate-500 border-r border-slate-200"
                            style={{
                              padding: "3px 10px",
                            }}
                          >
                            {it.ischecked
                              ? "Yes"
                              : "No"}
                          </td>

                          <td
                            style={{
                              padding: "3px 10px",
                            }}
                          >

                            <div className="flex items-center justify-center">

                              <motion.button
                                whileTap={{
                                  scale: 0.9,
                                }}
                                onClick={(e) =>
                                  handleToggleStatus(
                                    it,
                                    e
                                  )
                                }
                                title={
                                  it.page_status === 1
                                    ? "Active — click to deactivate"
                                    : "Inactive — click to activate"
                                }
                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition ${
                                  it.page_status === 1
                                    ? "bg-[#0EA5A4]/10 text-[#0B7E7D] hover:bg-[#0EA5A4]/20"
                                    : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                                }`}
                              >

                                {it.page_status ===
                                1 ? (
                                  <CheckCircle2
                                    size={15}
                                  />
                                ) : (
                                  <XCircle
                                    size={15}
                                  />
                                )}

                                {it.page_status ===
                                1
                                  ? "Active"
                                  : "Inactive"}

                              </motion.button>

                            </div>

                          </td>

                        </motion.tr>

                      ))}

                    </AnimatePresence>

                  </React.Fragment>

                ))}

              </tbody>

            </table>

          </div>

        )}

      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <AnimatePresence>

        {toast && (

          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              y: 20,
            }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success"
                ? "bg-[#0B2545] text-white"
                : "bg-red-600 text-white"
            }`}
          >
            {toast.message}

          </motion.div>

        )}

      </AnimatePresence>

    </div>
  );
}